import { defineStore } from 'pinia';
import { reactive, ref } from 'vue';
import { KycTypes } from '@/types/kyc';
import { CountryTypes } from '@/types/country';
import WidgetApiService from '@/common/services/widget-api.service';
import { uploadKycFilesToS3 } from '@/common/services/kyc-upload.service';
import { TransactTypes } from '@/types/transact';
var Countries = CountryTypes.Countries;
var TransactionType = TransactTypes.TransactionType;
export const useKycStore = defineStore('kyc', () => {
    const currentStep = ref(KycTypes.KycSteps.VERIFICATION_STATUS);
    const kycStatus = ref(KycTypes.KycStatus.INCOMPLETE);
    const idType = ref('');
    const idNumber = ref('');
    const additionalIdType = ref();
    const additionalIdNumber = ref();
    const kycResetKey = ref(0);
    const statesRefreshKey = ref(0);
    const didUri = ref();
    const issueKCC = ref(false);
    const requireDoubleIdNG = ref(true);
    const maskedEmail = ref('');
    const statesCities = reactive([]);
    const personal = {
        firstNames: '',
        lastName: '',
        day: '',
        month: '',
        year: '',
        mobileNumber: '',
        countryCode: '+234',
    };
    const personalDetails = ref(personal);
    const residential = {
        street: '',
        state: 'Abia',
        city: '',
        country: CountryTypes.Countries.NIGERIA,
        postalCode: '',
    };
    const residentialDetails = ref(residential);
    const fund = {
        funds: [],
        otherSelected: false,
        otherFunds: '',
    };
    const fundDetails = ref(fund);
    function DetermineKYCState() {
        switch (kycStatus.value) {
            case KycTypes.KycStatus.APPROVED:
                currentStep.value = KycTypes.KycSteps.VERIFICATION_STATUS;
                break;
            case KycTypes.KycStatus.UNDER_REVIEW:
            case KycTypes.KycStatus.PENDING_COMPLIANCE:
                currentStep.value = KycTypes.KycSteps.VERIFICATION_STATUS;
                break;
            case KycTypes.KycStatus.BLOCKED:
                currentStep.value = KycTypes.KycSteps.BLOCKED;
                break;
            case KycTypes.KycStatus.REJECTED:
                currentStep.value = KycTypes.KycSteps.VERIFICATION_STATUS;
                break;
            case KycTypes.KycStatus.INCOMPLETE:
                currentStep.value = KycTypes.KycSteps.KYC_1;
                break;
            default:
                currentStep.value = KycTypes.KycSteps.VERIFICATION_STATUS;
        }
    }
    async function BeginKYC(images, user, partnerSessionId, deviceId, partnerParams, userToken, idUpload) {
        try {
            if (!userToken) {
                throw new Error('cannot update user without token');
            }
            if (!user.dob ||
                !user.firstName ||
                !user.lastName ||
                !user.email ||
                !idType.value ||
                !idNumber.value ||
                !user.country) {
                throw new Error('Required kyc properties not submitted');
            }
            delete user.updatedAt;
            delete user.createdAt;
            // liveness check images
            const imagesArray = [];
            const uploaded = await uploadKycFilesToS3(images, userToken, partnerSessionId, deviceId);
            uploaded.forEach(image => {
                if (image && image.res) {
                    imagesArray.push({
                        image: image.res,
                        image_type_id: image.image_type_id,
                    });
                }
            });
            // id upload images
            const uploadsArray = [];
            switch (idType.value) {
                case CountryTypes.IdTypes.NIN:
                case CountryTypes.IdTypes.ID_BOOK:
                case CountryTypes.IdTypes.NATIONAL_ID:
                case CountryTypes.IdTypes.ALIEN_CARD:
                case CountryTypes.IdTypes.ECOWAS_ID:
                case CountryTypes.IdTypes.PASSPORT:
                    // An empty array is truthy — guard on length so an optional, absent
                    // id-doc batch never fires a request for zero files (the KYC service
                    // rejects an empty `files` array).
                    if (idUpload && idUpload.length) {
                        const idUploadArray = await uploadKycFilesToS3(idUpload, userToken, partnerSessionId, deviceId);
                        idUploadArray.forEach(image => {
                            if (image && image.res) {
                                const fileType = image.file.type;
                                const imageType = image.image_type_id;
                                uploadsArray.push({
                                    image: image.res,
                                    image_type: imageType === 3 ? 'front' : 'back',
                                    content_type: fileType,
                                });
                            }
                        });
                    }
                    break;
                default:
            }
            const mobileNumber = personalDetails.value.mobileNumber.startsWith('0')
                ? personalDetails.value.mobileNumber.slice(1)
                : personalDetails.value.mobileNumber;
            await WidgetApiService.BeginKYC(userToken, partnerSessionId, deviceId, {
                tier: 1,
                dob: user.dob,
                first_name: user.firstName,
                last_name: user.lastName,
                id_number: idNumber.value,
                id_type: requireDoubleIdNG.value ? 'BVN_NIN' : idType.value,
                bvnNumber: additionalIdNumber.value,
                ninNumber: idNumber.value,
                backfilledNigeriaPSP: user.backfilledNigeriaPSP ?? false,
                additional_id_number: additionalIdNumber.value,
                additional_id_type: additionalIdType.value,
                phone: `${personalDetails.value.countryCode}${mobileNumber}`,
                country: user.country,
                sourceOfFunds: fundDetails.value.funds,
                address: user?.address,
                images: imagesArray,
                partner_params: { ...partnerParams, platform: 'widget' },
                uploads: uploadsArray,
                didUri: didUri.value,
            });
        }
        catch (e) {
            throw new Error(`${e.message}`);
        }
    }
    function setKycState(status) {
        kycStatus.value = status;
        DetermineKYCState();
    }
    async function GetKycStatus(partnerSessionId, deviceId, userToken) {
        try {
            if (!userToken) {
                throw new Error('cannot update user without token');
            }
            const { status } = await WidgetApiService.GetKycStatus(userToken, partnerSessionId, deviceId);
            if (status !== kycStatus.value)
                kycStatus.value = status;
            return status;
        }
        catch (e) {
            console.error('failed to get KYC status');
            throw new Error('Failed to get kyc status');
        }
    }
    function setFromQueryParams(query) {
        let sourceOfFunds = [];
        if (typeof query.sourceOfFunds === 'string') {
            sourceOfFunds.push(query.sourceOfFunds);
        }
        else if (Array.isArray(query.sourceOfFunds)) {
            sourceOfFunds = query.sourceOfFunds;
        }
        personalDetails.value = {
            firstNames: query.firstname ?? '',
            lastName: query.lastname ?? '',
            day: query.day ?? '',
            month: query.month ?? '',
            year: query.year ?? '',
            mobileNumber: query.phone ?? '',
            countryCode: '+234',
        };
        residentialDetails.value = {
            street: query.street ?? '',
            state: query.state ?? '',
            city: query.city ?? '',
            country: Countries.NIGERIA,
            postalCode: query.postalCode ?? '',
        };
        fundDetails.value = {
            funds: sourceOfFunds,
            otherSelected: !!query.otherSourceOfFunds,
            otherFunds: query.otherSourceOfFunds ?? '',
        };
        idType.value = query.idType ?? '';
        idNumber.value = query.idNumber ?? '';
        additionalIdType.value = query.additionalIdType;
        additionalIdNumber.value = query.additionalIdNumber;
        if (query.did && (!query.txType || query.txType !== TransactionType.KYC)) {
            throw new Error('txType and did mismatch');
        }
        if (query.txType === TransactionType.KYC && !query.did) {
            throw new Error('did required');
        }
        didUri.value = query.did ?? undefined;
    }
    async function CheckForExistingId(token, sessionId, deviceId) {
        try {
            return await WidgetApiService.CheckForExistingId(token, sessionId, deviceId, {
                id_number: idNumber.value,
                additional_id_number: additionalIdNumber.value,
            });
        }
        catch (e) {
            console.error('Failed to check for existing ID');
            throw new Error('Failed to check for existing ID');
        }
    }
    function resetStore() {
        idType.value = '';
        idNumber.value = '';
        additionalIdType.value = undefined;
        additionalIdNumber.value = undefined;
        personalDetails.value = personal;
        residentialDetails.value = residential;
        fundDetails.value = fund;
        maskedEmail.value = '';
        kycResetKey.value += 1;
    }
    return {
        currentStep,
        kycStatus,
        setKycState,
        setFromQueryParams,
        idType,
        idNumber,
        personalDetails,
        kycResetKey,
        statesRefreshKey,
        residentialDetails,
        fundDetails,
        resetStore,
        BeginKYC,
        GetKycStatus,
        statesCities,
        issueKCC,
        didUri,
        additionalIdType,
        additionalIdNumber,
        requireDoubleIdNG,
        CheckForExistingId,
        maskedEmail,
    };
});
export default useKycStore;
